import React from 'react';
import { motion } from 'framer-motion';
import { Bell, Shield, Eye, Cpu, Network, BarChart2, Brain, Clock, Zap, Lock, CheckCircle, ArrowRight, AlertTriangle, Database } from 'lucide-react';

interface SecurityOperationsCenterProps {
  setCurrentPage: (page: string) => void;
}

const SecurityOperationsCenter: React.FC<SecurityOperationsCenterProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Eye,
      title: "24/7 Monitoring",
      description: "Continuous monitoring of your network, systems, and applications to detect potential security threats.",
      items: [
        "Real-time threat detection",
        "Network monitoring",
        "System surveillance",
        "Application security"
      ]
    },
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Advanced machine learning algorithms to identify patterns and predict potential security incidents.",
      items: [
        "Pattern recognition",
        "Anomaly detection",
        "Predictive analytics",
        "Behavioral analysis"
      ]
    },
    {
      icon: Zap,
      title: "Rapid Response",
      description: "Swift incident response protocols to contain and mitigate security threats in real-time.",
      items: [
        "Incident containment",
        "Threat elimination",
        "System recovery",
        "Post-incident analysis"
      ]
    },
    {
      icon: Lock,
      title: "Threat Prevention",
      description: "Proactive measures to prevent security breaches before they impact your operations.",
      items: [
        "Preventive controls",
        "Security hardening",
        "Vulnerability management",
        "Risk mitigation"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Shield,
      title: "Security Operations",
      description: "Comprehensive security monitoring and management.",
      items: [
        "24/7 security monitoring",
        "Incident management",
        "Threat hunting",
        "Security analytics"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Incident Response",
      description: "Swift and effective response to security incidents.",
      items: [
        "Rapid detection",
        "Incident containment",
        "Threat elimination",
        "Recovery support"
      ]
    },
    {
      icon: Database,
      title: "Threat Intelligence",
      description: "Advanced threat detection and analysis capabilities.",
      items: [
        "Threat feeds integration",
        "Intelligence analysis",
        "Threat correlation",
        "Risk assessment"
      ]
    }
  ];

  const technologies = [
    {
      icon: Network,
      title: "SIEM Integration",
      description: "Security Information and Event Management system for comprehensive log analysis and correlation.",
      features: [
        "Log aggregation",
        "Event correlation",
        "Security analytics",
        "Compliance reporting"
      ]
    },
    {
      icon: Cpu,
      title: "EDR Solutions",
      description: "Endpoint Detection and Response tools for advanced endpoint protection and threat hunting.",
      features: [
        "Endpoint monitoring",
        "Threat detection",
        "Automated response",
        "Forensic analysis"
      ]
    },
    {
      icon: BarChart2,
      title: "Analytics Platform",
      description: "Advanced analytics and reporting platform for security metrics and insights.",
      features: [
        "Security metrics",
        "Performance analytics",
        "Trend analysis",
        "Custom reporting"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Bell className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Security Operations Center (SOC)</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced threat detection, monitoring, and response capabilities to protect your organization 24/7
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Capabilities</h2>
            <p className="text-lg text-muted">
              Comprehensive security operations capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{capability.description}</p>

                <ul className="space-y-3">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Technologies Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Technologies</h2>
            <p className="text-lg text-muted">
              State-of-the-art security technologies and platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <tech.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{tech.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{tech.description}</p>

                <ul className="space-y-3">
                  {tech.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Enhance Your Security Operations?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Get started with our SOC services and protect your organization with 24/7 security monitoring
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SecurityOperationsCenter;